import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
// const API_URL = 'https://ujfyi-102-176-105-22.a.free.pinggy.link'; //the backend port

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  withCredentials: true, // MANDATORY for login to work across domains
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If error is 401 and we haven't tried to refresh token yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refresh_token');
        const response = await axios.post(`${API_URL}/auth/refresh/`, {
          refresh: refreshToken,
        });

        const { access } = response.data;
        localStorage.setItem('access_token', access);

        // Retry original request with new token
        originalRequest.headers.Authorization = `Bearer ${access}`;
        return api(originalRequest);
      } catch (refreshError) {
        // Refresh failed, logout user
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  login: (credentials) => api.post('/auth/login/', credentials),
  register: (userData) => api.post('/auth/register/', userData),
  getProfile: () => api.get('/auth/profile/'),
  updateProfile: (data) => api.patch('/auth/profile/', data),
  requestPasswordReset: (email) => api.post('/auth/password-reset/request/', { email }),
  confirmPasswordReset: (uid, token, newPassword, newPasswordConfirm) =>
    api.post('/auth/password-reset/confirm/', {
      uid, token, new_password: newPassword, new_password_confirm: newPasswordConfirm,
    }),
  exportAccountData: () => api.get('/auth/account/export/'),
  deleteAccount: (password) => api.delete('/auth/account/delete/', { data: { password } }),
};

// Research APIs
export const researchAPI = {
  getAll: (params) => api.get('/research/', { params }),
  getById: (id) => api.get(`/research/${id}/`),
  create: (data) => {
    const isFormData = data instanceof FormData;
    return api.post('/research/', data, {
      headers: isFormData ? { 'Content-Type': 'multipart/form-data' } : {},
    });
  },
  update: (id, data) => {
    const isFormData = data instanceof FormData;
    return api.patch(`/research/${id}/`, data, {
      headers: isFormData ? { 'Content-Type': 'multipart/form-data' } : {},
    });
  },
  delete: (id) => api.delete(`/research/${id}/`),
  submitForReview: (id) => api.post(`/research/${id}/submit_for_review/`),
  approve: (id) => api.post(`/research/${id}/approve/`),
  reject: (id, notes) => api.post(`/research/${id}/reject/`, { notes }),
  translate: (id) => api.post(`/research/${id}/translate/`),
  getRelated: (id) => api.get(`/research/${id}/related/`),
  search: (query) => api.get('/research/search/', { params: { q: query } }),
  getChartData: (id) => api.get(`/research/${id}/chart_data/`),
  getCsvColumns: (id) => api.get(`/research/${id}/csv_columns/`),
};

// Category APIs
export const categoryAPI = {
  getAll: () => api.get('/categories/'),
  getById: (id) => api.get(`/categories/${id}/`),
  getBySlug: (slug) => api.get(`/categories/${slug}/`),
  create: (data) => api.post('/categories/', data),
  update: (id, data) => api.put(`/categories/${id}/`, data),
  delete: (id) => api.delete(`/categories/${id}/`),
};

export const keyIndicatorAPI = {
  // placement: 'home' or 'datasets' -- filters server-side to just
  // what that specific page needs.
  getAll: (placement) => api.get('/key-indicators/', { params: placement ? { placement } : {} }),
  getById: (id) => api.get(`/key-indicators/${id}/`),
  create: (data) => api.post('/key-indicators/', data),
  update: (id, data) => api.patch(`/key-indicators/${id}/`, data),
  delete: (id) => api.delete(`/key-indicators/${id}/`),
};

export const subcategoryAPI = {
  getAll: (params) => api.get('/subcategories/', { params }),
  getById: (id) => api.get(`/subcategories/${id}/`),
  getBySlug: (slug) => api.get(`/subcategories/${slug}/`),
  create: (data) => api.post('/subcategories/', data),
  update: (id, data) => api.put(`/subcategories/${id}/`, data),
  delete: (id) => api.delete(`/subcategories/${id}/`),
};

// File APIs
export const fileAPI = {
  download: (fileId) => api.get(`/files/${fileId}/download/`, {
    responseType: 'blob',
  }),
  delete: (fileId) => api.delete(`/files/${fileId}/download/`),
};

// Favorites APIs
export const favoritesAPI = {
  getAll: () => api.get('/favorites/'),
  toggle: (researchId) => api.post('/favorites/toggle/', { research_id: researchId }),
  delete: (id) => api.delete(`/favorites/${id}/`),
};

// Dashboard APIs (Admin)
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats/'),
  getMyResearch: () => api.get('/dashboard/my-research/'),
};


// Dataset APIs
export const datasetCategoryAPI = {
  getAll: () => api.get('/dataset-categories/'),
  getById: (id) => api.get(`/dataset-categories/${id}/`),
  getBySlug: (slug) => api.get(`/dataset-categories/${slug}/`),
  create: (data) => api.post('/dataset-categories/', data),
  update: (id, data) => api.put(`/dataset-categories/${id}/`, data),
  delete: (id) => api.delete(`/dataset-categories/${id}/`),
};

export const datasetSubCategoryAPI = {
  getAll: (params) => api.get('/dataset-subcategories/', { params }),
  getById: (id) => api.get(`/dataset-subcategories/${id}/`),
  getBySlug: (slug) => api.get(`/dataset-subcategories/${slug}/`),
  create: (data) => api.post('/dataset-subcategories/', data),
  update: (id, data) => api.put(`/dataset-subcategories/${id}/`, data),
  delete: (id) => api.delete(`/dataset-subcategories/${id}/`),
};

export const datasetAPI = {
  getAll: (params) => api.get('/datasets/', { params }),
  getCatalogStats: () => api.get('/datasets/catalog_stats/'),
  getById: (id) => api.get(`/datasets/${id}/`),
  create: (data) => {
    // Check if data is FormData (contains files)
    const isFormData = data instanceof FormData;
    return api.post('/datasets/', data, {
      headers: isFormData ? {
        'Content-Type': 'multipart/form-data',
      } : {},
    });
  },
  update: (id, data) => {
    const isFormData = data instanceof FormData;
    return api.put(`/datasets/${id}/`, data, {
      headers: isFormData ? {
        'Content-Type': 'multipart/form-data',
      } : {},
    });
  },
  delete: (id) => api.delete(`/datasets/${id}/`),
  translate: (id) => api.post(`/datasets/${id}/translate/`),
  getChartData: (id) => api.get(`/datasets/${id}/chart_data/`),
  getCsvColumns: (id) => api.get(`/datasets/${id}/csv_columns/`),
  getCsvData: (id) => api.get(`/datasets/${id}/csv_data/`),
  getStatistics: (id) => api.get(`/datasets/${id}/statistics/`),
  getInsights: (id) => api.get(`/datasets/${id}/insights/`),
  getChartRecommendation: (id) => api.get(`/datasets/${id}/chart_recommendation/`),
  getDataReadiness: (id) => api.get(`/datasets/${id}/data_readiness/`),
  getGeojsonData: (id) => api.get(`/datasets/${id}/geojson_data/`),
  requestAccess: (id, purpose) => api.post(`/datasets/${id}/request_access/`, { purpose }),
  getAccessStatus: (id) => api.get(`/datasets/${id}/access_status/`),
  uploadFile: (datasetId, fileData) => {
    return api.post(`/datasets/${datasetId}/upload_file/`, fileData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
};

export const datasetFileAPI = {
  download: (fileId) => api.get(`/dataset-files/${fileId}/download/`, {
    responseType: 'blob',
  }),
  delete: (fileId) => api.delete(`/dataset-files/${fileId}/download/`),
};

export const episodeAPI = {
  getAll: (params) => api.get('/episodes/', { params }),
  getById: (id) => api.get(`/episodes/${id}/`),
  create: (data) => {
    const isFormData = data instanceof FormData;
    return api.post('/episodes/', data, {
      headers: isFormData ? { 'Content-Type': 'multipart/form-data' } : {},
    });
  },
  update: (id, data) => {
    const isFormData = data instanceof FormData;
    return api.patch(`/episodes/${id}/`, data, {
      headers: isFormData ? { 'Content-Type': 'multipart/form-data' } : {},
    });
  },
  delete: (id) => api.delete(`/episodes/${id}/`),
};

export const episodeTagAPI = {
  getAll: () => api.get('/episode-tags/'),
  create: (data) => api.post('/episode-tags/', data),
  delete: (id) => api.delete(`/episode-tags/${id}/`),
};

export const episodeFileAPI = {
  download: (episodeId) => api.get(`/episodes/${episodeId}/download/`, {
    responseType: 'blob',
  }),
};

export const assistantAPI = {
  query: (query) => api.post('/assistant/query/', { query }),
};

export const donationAPI = {
  initialize: (amount, email, name) => api.post('/donations/initialize/', { amount, email, name }),
  verify: (reference) => api.post('/donations/verify/', { reference }),
};

export const datasetPurchaseAPI = {
  initialize: (datasetId) => api.post('/dataset-purchases/initialize/', { dataset_id: datasetId }),
  verify: (reference) => api.post('/dataset-purchases/verify/', { reference }),
};

export const siteSettingsAPI = {
  get: () => api.get('/site-settings/'),
  update: (data) => {
    const isFormData = data instanceof FormData;
    return api.patch('/site-settings/', data, {
      headers: isFormData ? { 'Content-Type': 'multipart/form-data' } : {},
    });
  },
};

export const aboutPageAPI = {
  get: () => api.get('/about-page/'),
  update: (data) => api.patch('/about-page/', data),
};

export const companyProfileAPI = {
  get: () => api.get('/company-profile/'),
  update: (data) => api.patch('/company-profile/', data),
};

export const podcastPageSettingsAPI = {
  get: () => api.get('/podcast-page-settings/'),
  update: (data) => api.patch('/podcast-page-settings/', data),
};

export const datasetsPageSettingsAPI = {
  get: () => api.get('/datasets-page-settings/'),
  update: (data) => api.patch('/datasets-page-settings/', data),
};

export const downloadDataPageSettingsAPI = {
  get: () => api.get('/download-data-page-settings/'),
  update: (data) => api.patch('/download-data-page-settings/', data),
};

export const donatePageSettingsAPI = {
  get: () => api.get('/donate-page-settings/'),
  update: (data) => api.patch('/donate-page-settings/', data),
};

export const homePageSettingsAPI = {
  get: () => api.get('/homepage-settings/'),
  update: (data) => api.patch('/homepage-settings/', data),
};

export const footerSettingsAPI = {
  get: () => api.get('/footer-settings/'),
  update: (data) => api.patch('/footer-settings/', data),
};

export const newsletterAPI = {
  subscribe: (email, source) => api.post('/newsletter/subscribe/', { email, source }),
};

export const datasetAccessRequestAdminAPI = {
  getAll: (params) => api.get('/admin/dataset-access-requests/', { params }),
  approve: (id, notes) => api.post(`/admin/dataset-access-requests/${id}/approve/`, { notes }),
  deny: (id, notes) => api.post(`/admin/dataset-access-requests/${id}/deny/`, { notes }),
};

export const userManagementAPI = {
  getAll: (params) => api.get('/admin/users/', { params }),
  update: (id, data) => api.patch(`/admin/users/${id}/`, data),
};

export const donationAdminAPI = {
  getAll: (params) => api.get('/admin/donations/', { params }),
  getStats: () => api.get('/admin/donations/stats/'),
};

export const newsletterSubscriberAdminAPI = {
  getAll: (params) => api.get('/admin/newsletter-subscribers/', { params }),
  getStats: () => api.get('/admin/newsletter-subscribers/stats/'),
};

export const articleJournalAPI = {
  getAll: (params) => api.get('/article-journals/', { params }),
  getById: (id) => api.get(`/article-journals/${id}/`),
  create: (data) => {
    const isFormData = data instanceof FormData;
    return api.post('/article-journals/', data, {
      headers: isFormData ? { 'Content-Type': 'multipart/form-data' } : {},
    });
  },
  update: (id, data) => {
    const isFormData = data instanceof FormData;
    return api.patch(`/article-journals/${id}/`, data, {
      headers: isFormData ? { 'Content-Type': 'multipart/form-data' } : {},
    });
  },
  delete: (id) => api.delete(`/article-journals/${id}/`),
  submitForReview: (id) => api.post(`/article-journals/${id}/submit_for_review/`),
  approve: (id) => api.post(`/article-journals/${id}/approve/`),
  reject: (id, notes) => api.post(`/article-journals/${id}/reject/`, { notes }),
  translate: (id) => api.post(`/article-journals/${id}/translate/`),
  search: (query) => api.get('/article-journals/search/', { params: { q: query } }),
};

export const articleJournalFileAPI = {
  download: (fileId) => api.get(`/article-journal-files/${fileId}/download/`, {
    responseType: 'blob',
  }),
  delete: (fileId) => api.delete(`/article-journal-files/${fileId}/download/`),
};

export const currentAffairAPI = {
  getAll: (params) => api.get('/current-affairs/', { params }),
  getById: (id) => api.get(`/current-affairs/${id}/`),
  create: (data) => {
    const isFormData = data instanceof FormData;
    return api.post('/current-affairs/', data, {
      headers: isFormData ? { 'Content-Type': 'multipart/form-data' } : {},
    });
  },
  update: (id, data) => {
    const isFormData = data instanceof FormData;
    return api.patch(`/current-affairs/${id}/`, data, {
      headers: isFormData ? { 'Content-Type': 'multipart/form-data' } : {},
    });
  },
  delete: (id) => api.delete(`/current-affairs/${id}/`),
  submitForReview: (id) => api.post(`/current-affairs/${id}/submit_for_review/`),
  approve: (id) => api.post(`/current-affairs/${id}/approve/`),
  reject: (id, notes) => api.post(`/current-affairs/${id}/reject/`, { notes }),
  translate: (id) => api.post(`/current-affairs/${id}/translate/`),
  search: (query) => api.get('/current-affairs/search/', { params: { q: query } }),
};

export const currentAffairFileAPI = {
  download: (fileId) => api.get(`/current-affair-files/${fileId}/download/`, {
    responseType: 'blob',
  }),
  delete: (fileId) => api.delete(`/current-affair-files/${fileId}/download/`),
};

// Flexible admin-created Pages -- looked up by slug, not id
export const pageAPI = {
  getAll: () => api.get('/pages/'),
  getBySlug: (slug) => api.get(`/pages/${slug}/`),
  create: (data) => api.post('/pages/', data),
  update: (slug, data) => api.patch(`/pages/${slug}/`, data),
  delete: (slug) => api.delete(`/pages/${slug}/`),
  getNavbarPages: () => api.get('/pages-navbar/'),
  uploadImage: (file) => {
    const formData = new FormData();
    formData.append('image', file);
    return api.post('/pages/upload-image/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

export const archiveAPI = {
  search: (query) => api.get('/admin/archive-search/', { params: { q: query } }),
};

export const siteDocumentAPI = {
  // A missing document (404) is a normal, expected state -- not
  // uploaded yet -- not a real error, so validateStatus tells axios
  // to resolve normally instead of throwing. Callers check
  // res.status === 200 rather than relying on try/catch.
  get: (documentType) => api.get(`/site-documents/${documentType}/`, {
    validateStatus: (statusCode) => statusCode === 200 || statusCode === 404,
  }),
  upload: (documentType, file, title) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('title', title);
    return api.put(`/site-documents/${documentType}/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

export const publicationsAPI = {
  list: (params) => api.get('/admin/publications/', { params }),
};

export default api;

